/**
 * @fileoverview Run-only rift hook for chat query results.
 *
 * A chat `runQuery` result is an immutable run with no owning dataset. This
 * hook registers a plain {@link RiftDescriptor} (`datasetId: null`,
 * `riftConfig: null` → `SELECT * FROM '{runId}.arrow'`) so the result renders
 * through the shared rift query layer (`useRiftQuery`) exactly like a dataset
 * or block table. It is the seam for future chat blocks: passing a `riftConfig`
 * here lights up the full transform + stat-batching surface with no new
 * plumbing.
 *
 * Re-execution remap: when an expired run is re-run (see `runs.reexecute`), the
 * old runId is mapped to the fresh one so historical chat messages hydrate
 * against the new immutable result without rewriting the persisted message.
 */

import { observable } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import { rift } from "@repo/shared/rift";

import {
  getVersionedRiftId,
  type RiftDescriptor,
  registerRiftDescriptor,
} from "../dataset-rifts";

/** old runId -> fresh runId produced by a re-execution. Session-scoped. */
const runIdRemap = new Map<string, string>();

/**
 * Version counter bumped whenever a remap is recorded, so mounted `useRunRift`
 * (and `useRunChartRift`) hooks re-derive their descriptor against the fresh
 * runId.
 */
export const runRemapVersion$ = observable(0);

/** Resolves a runId through the re-execution remap (identity when unmapped). */
export const resolveRunId = (runId: string): string =>
  runIdRemap.get(runId) ?? runId;

/**
 * Records an old→new runId mapping from a re-execution and notifies mounted
 * consumers so they re-hydrate against the fresh run.
 */
export const recordRunRemap = (oldRunId: string, newRunId: string): void => {
  if (!oldRunId || oldRunId === newRunId) return;
  runIdRemap.set(oldRunId, newRunId);
  runRemapVersion$.set((v) => v + 1);
};

/** The computed result of {@link useRunRift}. */
export type RunRiftResult = {
  /** Rift cache id for the (possibly remapped) run, or `null` when no runId. */
  riftId: string | null;
  /** The runId the table hydrates against, after applying the remap. */
  resolvedRunId: string | null;
};

/**
 * Reactively derives the run-only `riftId` for a chat query result and registers
 * its descriptor. Re-derives when the runId is remapped by a re-execution.
 */
export const useRunRift = (runId: string | null): RunRiftResult => {
  const result = useValue<{
    riftId: string | null;
    resolvedRunId: string | null;
    descriptor: RiftDescriptor | null;
  }>(() => {
    // Subscribe to the remap version so a re-execution re-derives the id.
    runRemapVersion$.get();

    if (!runId) return { riftId: null, resolvedRunId: null, descriptor: null };

    const resolvedRunId = resolveRunId(runId);
    const sql = rift(`${resolvedRunId}.arrow`, null, null);
    const riftId = getVersionedRiftId(sql, resolvedRunId);
    const descriptor: RiftDescriptor = {
      riftId,
      datasetId: null,
      runId: resolvedRunId,
      sql,
      riftConfig: null,
      boardFilters: null,
      columnsConfig: null,
      columnDefs: null,
    };
    return { riftId, resolvedRunId, descriptor };
  });

  if (result.descriptor) registerRiftDescriptor(result.descriptor);

  return { riftId: result.riftId, resolvedRunId: result.resolvedRunId };
};
